import { ExtensionConceptSummary, TaxonomyChild } from 'app/api/tag-queries/types';

export function mergeExtensionsIntoPresentationTree(
  rawTaxonomyData: any,
  extensions: ExtensionConceptSummary[] | undefined,
  extensionPrefix: string | null
): { taxonomyData: any; extensionConceptNames: string[] } {
  void extensionPrefix;

  if (!rawTaxonomyData || !extensions || extensions.length === 0) {
    return { taxonomyData: rawTaxonomyData, extensionConceptNames: [] };
  }

  // Group extensions by wider-anchor concept name.
  const byWiderAnchor = new Map<string, ExtensionConceptSummary[]>();
  for (const ext of extensions) {
    const widerName = ext.widerAnchor?.name;
    if (!widerName) continue;
    const existing = byWiderAnchor.get(widerName) ?? [];
    existing.push(ext);
    byWiderAnchor.set(widerName, existing);
  }

  const extensionConceptNames: string[] = extensions.map((e) => e.name);

  const toTreeNode = (ext: ExtensionConceptSummary): TaxonomyChild => ({
    id: ext.name,
    labels: ext.labels ?? {},
    isAbstract: false,
    conceptType: ext.conceptType as TaxonomyChild['conceptType'],
    children: [],
    order: 999,
  });

  const visitChildren = (children: TaxonomyChild[]): TaxonomyChild[] =>
    children.map((child) => {
      const matches = byWiderAnchor.get(child.id);
      const mappedChildren = visitChildren(child.children ?? []);
      if (matches) {
        return { ...child, children: [...mappedChildren, ...matches.map(toTreeNode)] };
      }
      return child === (child.children as any) ? child : { ...child, children: mappedChildren };
    });

  const networks = rawTaxonomyData.presentationTree?.networks ?? [];
  const mergedNetworks = networks.map((network: any) => ({
    ...network,
    roots: (network.roots ?? []).map((root: any) => {
      const matches = byWiderAnchor.get(root.id);
      const mappedChildren = visitChildren(root.children ?? []);
      if (matches) {
        return { ...root, children: [...mappedChildren, ...matches.map(toTreeNode)] };
      }
      return { ...root, children: mappedChildren };
    }),
  }));

  return {
    taxonomyData: {
      ...rawTaxonomyData,
      presentationTree: {
        ...rawTaxonomyData.presentationTree,
        networks: mergedNetworks,
      },
    },
    extensionConceptNames,
  };
}
